
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { PaymentSuccess } from '@/components/payment/PaymentSuccess';
import { PaymentProcessing } from '@/components/payment/PaymentProcessing';
import { PaymentError } from '@/components/payment/PaymentError';
import { Button } from '@/components/ui/button';
import { useCart } from '@/contexts/CartContext';
import type { PaymentDetails } from '@/types/payment';

const MAX_POLLS = 30;
const POLL_INTERVAL = 3000;

const buildPaymentDetailsFromVerification = (data: any): PaymentDetails => {
  const hasBookings = (data.booking_ids?.length || 0) > 0;
  const hasPermits = (data.permit_ids?.length || 0) > 0;
  const metadata = data.metadata || {};

  return {
    id: data.session_id,
    status: 'complete',
    paymentStatus: 'paid',
    customerEmail: metadata.customer_email || null,
    customerName: metadata.customer_name || null,
    amountTotal: metadata.amount_total || null,
    currency: 'usd',
    paymentMethod: metadata.payment_method || null,
    permitsCreated: hasPermits,
    bookingsCreated: hasBookings,
    createdPermitIds: data.permit_ids || [],
    createdBookingIds: data.booking_ids || [],
    webhookProcessed: Boolean(data.webhook_processed_at),
    webhookProcessedAt: data.webhook_processed_at || undefined,
    itemType: hasBookings && !hasPermits ? 'rv-site' : 'fishing',
  };
};

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const sessionId = searchParams.get('session_id');

  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);

  // Ref-based polling to avoid re-render loops
  const pollCountRef = useRef(0);
  const [displayPollCount, setDisplayPollCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clear cart immediately on valid session
  useEffect(() => {
    if (sessionId) {
      console.log('[PaymentSuccessPage] Clearing cart for successful payment session:', sessionId);
      clearCart();
    }
  }, [sessionId, clearCart]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const pollPaymentStatus = useCallback(async () => {
    if (!sessionId) return;

    const currentPoll = pollCountRef.current;
    console.log(`[PaymentSuccess] Poll attempt ${currentPoll + 1}/${MAX_POLLS}`);

    try {
      // Poll payment_verifications table directly — no edge function needed
      const { data, error: queryError } = await supabase
        .from('payment_verifications')
        .select('*')
        .eq('session_id', sessionId)
        .maybeSingle();

      if (queryError) {
        console.error('[PaymentSuccess] DB query error:', queryError);
        throw new Error(queryError.message);
      }

      // Check if webhook has processed this payment
      if (data?.payment_status === 'paid' && data?.webhook_processed_at) {
        console.log('[PaymentSuccess] Payment verified via DB polling:', data);

        // Also check if permits/bookings exist in their tables as a fallback
        let permitIds = data.permit_ids || [];
        let bookingIds = data.booking_ids || [];

        // If verification row doesn't have IDs yet, do a direct lookup
        if (permitIds.length === 0 && bookingIds.length === 0) {
          const [permitsResult, bookingsResult] = await Promise.all([
            supabase
              .from('customer_permits')
              .select('permit_id')
              .eq('payment_id', sessionId)
              .eq('payment_status', 'paid'),
            supabase
              .from('site_bookings')
              .select('booking_id')
              .eq('payment_id', sessionId)
              .eq('payment_status', 'paid'),
          ]);

          permitIds = permitsResult.data?.map((p) => p.permit_id).filter(Boolean) || [];
          bookingIds = bookingsResult.data?.map((b) => b.booking_id).filter(Boolean) || [];
        }

        const details = buildPaymentDetailsFromVerification({
          ...data,
          permit_ids: permitIds,
          booking_ids: bookingIds,
        });

        setPaymentDetails(details);
        setIsProcessing(false);
        stopPolling();
        return;
      }

      // Also check if payment is paid but webhook_processed_at not yet set
      // (permits/bookings may already exist from the webhook)
      if (data && currentPoll > 5) {
        const [permitsResult, bookingsResult] = await Promise.all([
          supabase
            .from('customer_permits')
            .select('permit_id')
            .eq('payment_id', sessionId)
            .eq('payment_status', 'paid'),
          supabase
            .from('site_bookings')
            .select('booking_id')
            .eq('payment_id', sessionId)
            .eq('payment_status', 'paid'),
        ]);

        const permitIds = permitsResult.data?.map((p) => p.permit_id).filter(Boolean) || [];
        const bookingIds = bookingsResult.data?.map((b) => b.booking_id).filter(Boolean) || [];

        if (permitIds.length > 0 || bookingIds.length > 0) {
          console.log('[PaymentSuccess] Found records via direct DB lookup:', { permitIds, bookingIds });

          const details = buildPaymentDetailsFromVerification({
            ...data,
            payment_status: 'paid',
            permit_ids: permitIds,
            booking_ids: bookingIds,
            webhook_processed_at: new Date().toISOString(),
          });

          setPaymentDetails(details);
          setIsProcessing(false);
          stopPolling();
          return;
        }
      }

      // Timeout check
      if (currentPoll >= MAX_POLLS - 1) {
        console.error('[PaymentSuccess] Polling timeout reached');
        setShowRecovery(true);
        setError('Payment processing is taking longer than expected. Your payment was successful — you will receive a confirmation email shortly.');
        setIsProcessing(false);
        stopPolling();
        return;
      }

      // Increment poll counter
      pollCountRef.current += 1;
      setDisplayPollCount(pollCountRef.current);
    } catch (err) {
      console.error('[PaymentSuccess] Polling error:', err);
      // Don't stop on transient errors until we've exhausted polls
      if (currentPoll >= MAX_POLLS - 1) {
        setError(err instanceof Error ? err.message : 'Payment verification failed');
        setIsProcessing(false);
        setShowRecovery(true);
        stopPolling();
      } else {
        pollCountRef.current += 1;
        setDisplayPollCount(pollCountRef.current);
      }
    }
  }, [sessionId, stopPolling]);

  // Single stable polling effect — no pollCount dependency
  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided');
      setIsProcessing(false);
      return;
    }

    console.log('[PaymentSuccess] Starting DB-based payment verification for session:', sessionId);
    pollCountRef.current = 0;
    setDisplayPollCount(0);

    // Poll immediately
    pollPaymentStatus();

    // Set up single stable interval
    intervalRef.current = setInterval(pollPaymentStatus, POLL_INTERVAL);

    return () => {
      stopPolling();
    };
  }, [sessionId]); // Only sessionId — stable interval

  const handleReturnHome = () => {
    clearCart();
    navigate('/');
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Payment Session</h1>
          <p className="text-gray-600 mb-6">No session ID was provided.</p>
          <Button onClick={handleReturnHome}>Return Home</Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <PaymentError errorMessage={error} />

          {showRecovery && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">What To Do</h3>
              <p className="text-sm text-blue-700 mb-4">
                Your payment was successful. If you don't receive a confirmation email within 15 minutes, please contact us.
              </p>
            </div>
          )}

          <div className="mt-4">
            <Button onClick={handleReturnHome} variant="outline">
              Return Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return <PaymentProcessing sessionId={sessionId} pollCount={displayPollCount} maxPolls={MAX_POLLS} />;
  }

  if (!paymentDetails) {
    return <PaymentError errorMessage="No payment details available" />;
  }

  return <PaymentSuccess paymentDetails={paymentDetails} onReturnHome={handleReturnHome} />;
};

export default PaymentSuccessPage;
